/**
 * Matching modal — wide, split-pane.
 *
 *  - Left pane: the real preferences this client submitted during intake
 *    (read off the `ServiceRequest` extensions + Patient.communication). Admins
 *    can remove preferences to relax criteria and rerank live (session-only).
 *  - Right pane: providers with open referral capacity (live FHIR Tasks), ranked
 *    against the (relaxed) preferences with the shared intake matcher.
 */

import {
  Alert,
  Avatar,
  Badge,
  Button,
  Center,
  CloseButton,
  Collapse,
  Divider,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconCircleCheck,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { MedplumClient } from '@medplum/core';
import type { Practitioner, ServiceRequest, Task } from '@medplum/fhirtypes';
import { AVAILABILITY_PREF_EXT } from '@unify/fhir-model/constants';
import {
  getAreasOfFocus,
  getProviderPreferences,
  getSessionFormatPreference,
  type SessionFormatPreference,
} from '@unify/fhir-model/resources/service-request';
import {
  PREFERRED_LOCATIONS_EXT,
  THERAPY_APPROACHES_EXT,
} from '../../../lib/serviceRequestFhirConstants';
import { REFERRAL_REQUEST_TASK_CODE, TASK_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import {
  computeReferralRequestMetrics,
  getReferralAcceptsInPerson,
  getReferralNoCouplesFamilies,
  getReferralNoMinors,
  getReferralOnlyCashPay,
  getReferralProviderPractitionerIdForIntake,
  isReferralRequestTask,
} from '../../../fhir-model/resources/referral-task';
import {
  LANGUAGE_OPTIONS,
  PROVIDER_SPECIALTIES,
  THERAPY_TYPES,
} from '@unify/fhir-model/provider-profile-datasets';
import {
  AVAILABILITY_DAYS_OPTIONS,
  AVAILABILITY_TIME_OPTIONS,
} from '../../public/intake/bookingConstraints';
import type { MockProvider } from '../../public/intake/mockProviders';
import { getMatchedProviders, scoreProvider } from '../../public/intake/matching';
import { practitionerToIntakeProvider } from '../../public/intake/intakeFhirMapping';
import { formatPractitionerNameParts } from '@unify/fhir-model/resources/practitioner';
import { PractitionerNameText } from '@/components/PractitionerNameText';
import { INITIAL_ANSWERS, type IntakeAnswers } from '../../public/intake/types';

// ── Label lookups (code → human display) ────────────────────────────────────
const SPECIALTY_LABELS = new Map(PROVIDER_SPECIALTIES.map((o) => [o.code, o.display]));
const APPROACH_LABELS = new Map(THERAPY_TYPES.map((o) => [o.code, o.display]));
const LANGUAGE_LABELS = new Map(LANGUAGE_OPTIONS.map((o) => [o.code, o.display]));
const AVAILABILITY_LABELS = new Map<string, string>(
  [...AVAILABILITY_TIME_OPTIONS, ...AVAILABILITY_DAYS_OPTIONS].map((o) => [o.value, o.label]),
);
const SESSION_FORMAT_LABELS: Record<SessionFormatPreference, string> = {
  'in-person': 'In-person',
  virtual: 'Telehealth (virtual)',
  hybrid: 'Hybrid (in-person or virtual)',
  'no-preference': 'No preference',
};

// ── Provider matches (right pane), sourced from live referral capacity ──────
/** A provider's open referral request (capacity request), derived from a FHIR Task. */
interface ReferralRequestView {
  id: string;
  requestDate: string;
  referralType: string;
  availableSlots: number;
  acceptsInPerson: boolean;
  notes?: string;
}

interface ScoredProvider extends MockProvider {
  legalName?: string;
  matchScore: number;
  referralRequests: ReferralRequestView[];
}

/** Providers with open referral capacity + their open referral requests. */
interface MatchPool {
  providers: Array<MockProvider & { legalName?: string }>;
  requestsByProvider: Record<string, ReferralRequestView[]>;
}

const EMPTY_POOL: MatchPool = { providers: [], requestsByProvider: {} };

function referralTaskToView(task: Task): ReferralRequestView {
  const metrics = computeReferralRequestMetrics(task);
  const iso = task.authoredOn ?? task.meta?.lastUpdated ?? new Date().toISOString();
  const flags: string[] = [];
  if (getReferralNoMinors(task)) flags.push('No minors');
  if (getReferralNoCouplesFamilies(task)) flags.push('No couples/families');
  if (getReferralOnlyCashPay(task)) flags.push('Cash pay only');
  return {
    id: task.id!,
    requestDate: new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    referralType: 'Referral request',
    availableSlots: metrics.availableSlots,
    acceptsInPerson: getReferralAcceptsInPerson(task) ?? false,
    notes: flags.length > 0 ? flags.join(' · ') : undefined,
  };
}

/**
 * Live match pool — mirrors `listIntakeProviders`: only providers with open referral
 * capacity are matchable, keeping every open referral Task so each card lists them.
 * Medplum access policy scopes the search to what the signed-in admin may read.
 */
async function loadProviderMatchPool(medplum: MedplumClient): Promise<MatchPool> {
  const tasks = (await medplum.searchResources('Task', {
    code: `${TASK_TYPE_SYSTEM}|${REFERRAL_REQUEST_TASK_CODE}`,
    _count: '200',
    _sort: '-_lastUpdated',
  })) as Task[];

  const requestsByProvider: Record<string, ReferralRequestView[]> = {};
  const providerIds = new Set<string>();
  for (const task of tasks) {
    if (!task.id || !isReferralRequestTask(task)) continue;
    if (task.status !== 'requested' && task.status !== 'in-progress') continue;
    if (computeReferralRequestMetrics(task).availableSlots <= 0) continue;
    const providerId = getReferralProviderPractitionerIdForIntake(task);
    if (!providerId) continue;
    providerIds.add(providerId);
    (requestsByProvider[providerId] ??= []).push(referralTaskToView(task));
  }

  if (providerIds.size === 0) return EMPTY_POOL;

  const practitioners = (await medplum.searchResources('Practitioner', {
    _id: [...providerIds].join(','),
    _count: String(providerIds.size),
  })) as Practitioner[];

  return {
    providers: practitioners
      .filter((p) => p.id)
      .map((p) => {
        const vm = practitionerToIntakeProvider(p);
        const parts = formatPractitionerNameParts(p);
        return { ...vm, name: parts.primary || vm.name, legalName: parts.legal };
      }),
    requestsByProvider,
  };
}

// ── Editable client-preference criteria (session-only exploration) ──────────
const PREF_KEYS = [
  'sessionFormat',
  'areasOfFocus',
  'approaches',
  'providerPreferences',
  'availability',
  'language',
  'locations',
] as const;
type PrefKey = (typeof PREF_KEYS)[number];
type PrefRecord = Record<PrefKey, string[]>;

const emptyPrefRecord = (): PrefRecord => ({
  sessionFormat: [],
  areasOfFocus: [],
  approaches: [],
  providerPreferences: [],
  availability: [],
  language: [],
  locations: [],
});

const countValues = (record: PrefRecord): number =>
  PREF_KEYS.reduce((n, k) => n + record[k].length, 0);

// ── Helpers ─────────────────────────────────────────────────────────────────
function readJsonArrayExt(sr: ServiceRequest, url: string): string[] {
  const raw = sr.extension?.find((e) => e.url === url)?.valueString;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

/** Availability is stored as `{ availabilityTimeOfDay, availabilityDays }` or a flat array. */
function readAvailability(sr: ServiceRequest): string[] {
  const raw = sr.extension?.find((e) => e.url === AVAILABILITY_PREF_EXT)?.valueString;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as string[];
    const time = (parsed?.availabilityTimeOfDay as string[]) ?? [];
    const days = (parsed?.availabilityDays as string[]) ?? [];
    return [...time, ...days];
  } catch {
    return [];
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function scoreColor(score: number): string {
  if (score >= 85) return 'green';
  if (score >= 70) return 'yellow';
  return 'gray';
}

// ── Left pane: one labelled preference section ───────────────────────────────
function PreferenceSection({
  label,
  values,
  onRemove,
}: {
  label: string;
  values: string[];
  /** When provided, each value renders a removable `×` for criteria exploration. */
  onRemove?: (value: string) => void;
}): JSX.Element {
  return (
    <div>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase">
        {label}
      </Text>
      {values.length === 0 ? (
        <Text size="sm" c="dimmed" mt={4}>
          No preference
        </Text>
      ) : (
        <Group gap={6} mt={6}>
          {values.map((v) => (
            <Badge
              key={v}
              variant="light"
              radius="sm"
              size="sm"
              pr={onRemove ? 3 : undefined}
              rightSection={
                onRemove ? (
                  <CloseButton
                    size="xs"
                    radius="xl"
                    variant="transparent"
                    aria-label={`Remove ${v}`}
                    onClick={() => onRemove(v)}
                  />
                ) : undefined
              }
            >
              {v}
            </Badge>
          ))}
        </Group>
      )}
    </div>
  );
}

// ── Right pane: one open referral request row inside an expanded card ─────────
function ReferralRequestRow({
  request,
  onAssign,
}: {
  request: ReferralRequestView;
  onAssign: () => void;
}): JSX.Element {
  return (
    <Paper withBorder radius="sm" p="sm" bg="gray.0">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              {request.referralType}
            </Text>
            <Badge size="sm" variant="light" color={request.availableSlots > 0 ? 'teal' : 'gray'}>
              {request.availableSlots} {request.availableSlots === 1 ? 'slot' : 'slots'} open
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            Requested {request.requestDate} · {request.acceptsInPerson ? 'In-person OK' : 'Telehealth only'}
          </Text>
          {request.notes && (
            <Text size="xs" c="dark.5">
              {request.notes}
            </Text>
          )}
        </Stack>
        <Button size="xs" disabled={request.availableSlots <= 0} onClick={onAssign}>
          Assign
        </Button>
      </Group>
    </Paper>
  );
}

// ── Right pane: a single (expandable) provider match card ────────────────────
function ProviderMatchCard({
  provider,
  onAssign,
}: {
  provider: ScoredProvider;
  onAssign: (request: ReferralRequestView) => void;
}): JSX.Element {
  const [expanded, { toggle }] = useDisclosure(false);
  const formatLabel = provider.format
    .map((f) => (f === 'in-person' ? 'In-person' : 'Telehealth'))
    .join(' · ');

  return (
    <Paper withBorder radius="md" p="md">
      <Group
        align="flex-start"
        wrap="nowrap"
        gap="md"
        onClick={toggle}
        style={{ cursor: 'pointer' }}
      >
        <Avatar src={provider.headshotUrl} size={72} radius="md" color="gold">
          {initials(provider.name)}
        </Avatar>
        <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div>
              <PractitionerNameText fw={700} primary={provider.name} legal={provider.legalName} />
              <Text size="xs" c="dimmed">
                {provider.credentials} · {provider.location}
              </Text>
            </div>
            <Group gap="xs" wrap="nowrap">
              <Badge size="lg" color={scoreColor(provider.matchScore)} variant="filled">
                {provider.matchScore}% match
              </Badge>
              <IconChevronDown
                size={18}
                style={{
                  transition: 'transform 150ms ease',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                }}
              />
            </Group>
          </Group>

          <Group gap={6}>
            {provider.specialties.slice(0, 4).map((s) => (
              <Badge key={s} variant="light" radius="sm" size="sm">
                {s}
              </Badge>
            ))}
          </Group>

          <Group gap="lg">
            <Text size="xs" c="dark.6">
              {formatLabel}
            </Text>
            <Text size="xs" c="dark.6">
              {provider.languages.join(', ')}
            </Text>
          </Group>

          <Group gap="lg">
            <Text
              size="xs"
              c="dark.6"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <IconCircleCheck size={13} />
              {provider.acceptsInsurance ? 'Accepts insurance' : 'Out of pocket'}
            </Text>
            {provider.takingNewClients !== false && (
              <Text
                size="xs"
                c="dark.6"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <IconUsers size={13} />
                Taking new clients
              </Text>
            )}
          </Group>
        </Stack>
      </Group>

      <Collapse in={expanded}>
        <Divider my="md" />
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
          Open referral requests
        </Text>
        {provider.referralRequests.length === 0 ? (
          <Text size="sm" c="dimmed">
            No open referral requests.
          </Text>
        ) : (
          <Stack gap="xs">
            {provider.referralRequests.map((r) => (
              <ReferralRequestRow key={r.id} request={r} onAssign={() => onAssign(r)} />
            ))}
          </Stack>
        )}
      </Collapse>
    </Paper>
  );
}

export interface MatchingModalProps {
  open: boolean;
  onClose: () => void;
  serviceRequest?: ServiceRequest;
  clientId?: string;
  clientName: string;
  episodeServiceType: string;
  state?: string;
  payer?: string;
}

export function MatchingModal({
  open,
  onClose,
  serviceRequest,
  clientId,
  clientName,
  episodeServiceType,
  state,
  payer,
}: MatchingModalProps): JSX.Element {
  const medplum = useMedplum();
  const [languageLabel, setLanguageLabel] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [zip, setZip] = useState<string>('');
  const [locationNames, setLocationNames] = useState<string[]>([]);

  const prefs = useMemo(() => {
    if (!serviceRequest) return null;
    return {
      sessionFormat: getSessionFormatPreference(serviceRequest),
      areasOfFocus: (getAreasOfFocus(serviceRequest) ?? []).map((c) => SPECIALTY_LABELS.get(c) ?? c),
      approaches: readJsonArrayExt(serviceRequest, THERAPY_APPROACHES_EXT).map(
        (c) => APPROACH_LABELS.get(c) ?? c,
      ),
      providerPreferences: getProviderPreferences(serviceRequest) ?? [],
      availability: readAvailability(serviceRequest).map((v) => AVAILABILITY_LABELS.get(v) ?? v),
      preferredLocationIds: readJsonArrayExt(serviceRequest, PREFERRED_LOCATIONS_EXT).filter(
        (id) => id && id !== 'no-preference',
      ),
    };
  }, [serviceRequest]);

  // The client's full submitted criteria (display labels), including async-loaded
  // language + locations. Deletions are tracked separately so newly-loaded values
  // still flow in while removals persist.
  const initialPrefs = useMemo<PrefRecord>(
    () => ({
      sessionFormat: prefs?.sessionFormat ? [SESSION_FORMAT_LABELS[prefs.sessionFormat]] : [],
      areasOfFocus: prefs?.areasOfFocus ?? [],
      approaches: prefs?.approaches ?? [],
      providerPreferences: prefs?.providerPreferences ?? [],
      availability: prefs?.availability ?? [],
      language: languageLabel ? [languageLabel] : [],
      locations: locationNames,
    }),
    [prefs, languageLabel, locationNames],
  );

  const [removed, setRemoved] = useState<PrefRecord>(emptyPrefRecord);
  const [pool, setPool] = useState<MatchPool>(EMPTY_POOL);
  const [loadingPool, setLoadingPool] = useState(false);
  const [poolError, setPoolError] = useState<string | null>(null);

  // Values still in effect for a category = submitted minus removed.
  const activePrefs = useMemo<PrefRecord>(() => {
    const next = emptyPrefRecord();
    for (const key of PREF_KEYS) {
      next[key] = initialPrefs[key].filter((v) => !removed[key].includes(v));
    }
    return next;
  }, [initialPrefs, removed]);

  const hasChanges = countValues(removed) > 0;

  const removePref = useCallback((key: PrefKey, value: string) => {
    setRemoved((prev) =>
      prev[key].includes(value) ? prev : { ...prev, [key]: [...prev[key], value] },
    );
  }, []);

  const resetPrefs = useCallback(() => setRemoved(emptyPrefRecord()), []);

  // Build intake-style answers from the (relaxed) preferences so the existing
  // matcher reranks live as the admin removes constraints.
  const answers = useMemo<IntakeAnswers>(() => {
    const rawFormat = prefs?.sessionFormat;
    const formatActive = activePrefs.sessionFormat.length > 0;
    const format: IntakeAnswers['format'] =
      formatActive && (rawFormat === 'in-person' || rawFormat === 'virtual')
        ? rawFormat
        : 'no-preference';
    return {
      ...INITIAL_ANSWERS,
      specialties: activePrefs.areasOfFocus,
      topics: activePrefs.approaches,
      format,
      payType: payer ? 'insurance' : null,
      zip,
    };
  }, [prefs?.sessionFormat, activePrefs.sessionFormat, activePrefs.areasOfFocus, activePrefs.approaches, payer, zip]);

  const matchedProviders = useMemo<ScoredProvider[]>(() => {
    if (pool.providers.length === 0) return [];
    const ordered = getMatchedProviders(answers, pool.providers);
    const scores = ordered.map((p) => Math.max(0, scoreProvider(p, answers)));
    const maxScore = scores.reduce((m, s) => Math.max(m, s), 0);
    return ordered.map((p, i) => ({
      ...p,
      matchScore: maxScore <= 0 ? 100 : Math.min(99, Math.round((scores[i] / maxScore) * 100)),
      referralRequests: pool.requestsByProvider[p.id] ?? [],
    }));
  }, [pool, answers]);

  // Reset exploration whenever the modal (re)opens.
  useEffect(() => {
    if (open) setRemoved(emptyPrefRecord());
  }, [open]);

  // Load providers with open referral capacity + their open referral requests.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingPool(true);
    setPoolError(null);
    void (async () => {
      try {
        const result = await loadProviderMatchPool(medplum);
        if (!cancelled) setPool(result);
      } catch (e) {
        if (!cancelled) {
          setPool(EMPTY_POOL);
          setPoolError(e instanceof Error ? e.message : 'Failed to load providers.');
        }
      } finally {
        if (!cancelled) setLoadingPool(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, medplum]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    void (async () => {
      if (clientId) {
        try {
          const patient = await medplum.readResource('Patient', clientId);
          const code =
            patient.communication?.find((c) => c.preferred)?.language?.coding?.[0]?.code ??
            patient.communication?.[0]?.language?.coding?.[0]?.code;
          const phoneValue =
            patient.telecom?.find((t) => t.system === 'phone')?.value ?? null;
          if (!cancelled) {
            setLanguageLabel(code ? (LANGUAGE_LABELS.get(code) ?? code) : null);
            setPhone(phoneValue);
            setZip(patient.address?.[0]?.postalCode ?? '');
          }
        } catch {
          if (!cancelled) {
            setLanguageLabel(null);
            setPhone(null);
            setZip('');
          }
        }
      } else if (!cancelled) {
        setLanguageLabel(null);
        setPhone(null);
        setZip('');
      }

      const ids = prefs?.preferredLocationIds ?? [];
      if (ids.length > 0) {
        try {
          const locs = await medplum.searchResources('Location', `_id=${ids.join(',')}`);
          if (!cancelled) {
            setLocationNames(locs.map((l) => l.name ?? l.id ?? '').filter(Boolean));
          }
        } catch {
          if (!cancelled) setLocationNames([]);
        }
      } else if (!cancelled) {
        setLocationNames([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, clientId, medplum, prefs?.preferredLocationIds]);

  // TODO: replace with the real assignment write — append an `assigned-episode`
  // Task.output (disposition `pending`) to the referral Task (request.id) and set
  // EpisodeOfCare.careManager (ideally via the ticketing bot).
  const handleAssign = (provider: ScoredProvider, request: ReferralRequestView): void => {
    notifications.show({
      color: 'teal',
      title: 'Client assigned',
      message: `${clientName || 'Client'} assigned to ${provider.name}'s ${request.referralType}.`,
    });
    onClose();
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title="Match providers"
      size={1080}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Grid gutter="xl">
        {/* Left: client intake preferences */}
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Stack gap="md">
            <div>
              <Group gap="xs" align="baseline">
                <Text fw={700} size="lg">
                  {clientName}
                </Text>
                {phone && (
                  <Text size="sm" c="dimmed">
                    {phone}
                  </Text>
                )}
              </Group>
              <Group gap="xs" mt={4}>
                {episodeServiceType && (
                  <Badge variant="light" radius="sm">
                    {episodeServiceType}
                  </Badge>
                )}
                {state && (
                  <Badge variant="light" radius="sm" color="gray">
                    {state}
                  </Badge>
                )}
                {payer && (
                  <Badge variant="light" radius="sm" color="gray">
                    {payer}
                  </Badge>
                )}
              </Group>
            </div>

            <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
              <Divider label="Client preferences" labelPosition="left" style={{ flex: 1 }} />
              {hasChanges && (
                <Button
                  variant="subtle"
                  size="compact-xs"
                  leftSection={<IconRefresh size={14} />}
                  onClick={resetPrefs}
                >
                  Reset
                </Button>
              )}
            </Group>

            <Text size="xs" c="dimmed">
              Remove a preference to relax the requirements and refresh provider matches.
            </Text>

            <PreferenceSection
              label="Session format"
              values={activePrefs.sessionFormat}
              onRemove={(v) => removePref('sessionFormat', v)}
            />
            <PreferenceSection
              label="Areas of focus"
              values={activePrefs.areasOfFocus}
              onRemove={(v) => removePref('areasOfFocus', v)}
            />
            <PreferenceSection
              label="Approaches"
              values={activePrefs.approaches}
              onRemove={(v) => removePref('approaches', v)}
            />
            <PreferenceSection
              label="Provider preferences"
              values={activePrefs.providerPreferences}
              onRemove={(v) => removePref('providerPreferences', v)}
            />
            <PreferenceSection
              label="Availability"
              values={activePrefs.availability}
              onRemove={(v) => removePref('availability', v)}
            />
            <PreferenceSection
              label="Preferred language"
              values={activePrefs.language}
              onRemove={(v) => removePref('language', v)}
            />
            <PreferenceSection
              label="Preferred locations"
              values={activePrefs.locations}
              onRemove={(v) => removePref('locations', v)}
            />
          </Stack>
        </Grid.Col>

        {/* Right: providers with open referral capacity, ranked against preferences */}
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="md">
            <Divider label="Matched providers" labelPosition="left" />
            {loadingPool ? (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Finding matches…
                  </Text>
                </Stack>
              </Center>
            ) : poolError ? (
              <Alert color="red" title="Could not load providers">
                {poolError}
              </Alert>
            ) : matchedProviders.length === 0 ? (
              <Text size="sm" c="dimmed">
                No providers currently have open referral requests with available capacity.
              </Text>
            ) : (
              matchedProviders.map((p) => (
                <ProviderMatchCard
                  key={p.id}
                  provider={p}
                  onAssign={(request) => handleAssign(p, request)}
                />
              ))
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Modal>
  );
}
